import { useQueryClient } from "react-query";
import { axiosInstance } from "api/axios";
import useCustomMutation from "hooks/useCustomMutation";
import useCustomQuery from "hooks/useCustomQuery";
import { uploadCompanyEncryptedFile } from "utils/uploadCompanyEncryptedFile";
import {
  IRegistryInvoice,
  ICreateRegistryInvoiceRequest,
} from "dto/Invoices/IRegistryInvoice";
import { IRegistryInvoicePrefill } from "dto/Invoices/IRegistryInvoicePrefill";

/**
 * Rejestr faktur i rachunków — REST API (BE: RegistryInvoicesController).
 * Skan dokumentu jest szyfrowany po stronie klienta kluczem obiektu (zawiniętym
 * kluczem publicznym aktywnego profilu) i wgrywany przez /file/init + /file/confirm;
 * do BE trafia tylko FileUri + KeyId.
 */

// ---------------------------------------------------------------------------
// Funkcje API
// ---------------------------------------------------------------------------

const getInvoicesApi = async (
  searchText?: string,
): Promise<IRegistryInvoice[]> =>
  (
    await axiosInstance.get<IRegistryInvoice[]>("/registry/invoices", {
      params: { Text: searchText?.trim() || undefined },
    })
  ).data;

// Pojedyncza faktura po Id — to samo źródło co rejestr Faktur (do metryki faktury
// na poziomie pozycji w Budżet / Lista wydatków).
const getInvoiceApi = async (id: string): Promise<IRegistryInvoice> =>
  (await axiosInstance.get<IRegistryInvoice>(`/registry/invoices/${id}`)).data;

const createInvoiceApi = async (
  request: ICreateRegistryInvoiceRequest & { FileUri?: string; KeyId?: string },
): Promise<IRegistryInvoice> =>
  (await axiosInstance.post<IRegistryInvoice>("/registry/invoices", request))
    .data;

/**
 * Dane do wstępnego wypełnienia formularza „Faktura/Rachunek" na podstawie faktury
 * KSeF (funkcja „Księgowanie"). Pobierane na żądanie (klik), nie przy montażu.
 */
const getKsefAccountingPrefill = async (
  ksefNumber: string,
): Promise<IRegistryInvoicePrefill> =>
  (
    await axiosInstance.get<IRegistryInvoicePrefill>(
      `/ksef/invoice/${encodeURIComponent(ksefNumber)}/accounting-prefill`,
    )
  ).data;

// ---------------------------------------------------------------------------
// Typy
// ---------------------------------------------------------------------------

export interface ICreateRegistryInvoicePayload
  extends ICreateRegistryInvoiceRequest {
  file?: File | null;
}

// ---------------------------------------------------------------------------
// Klucze zapytań
// ---------------------------------------------------------------------------

const QUERY_KEY = "registryInvoices";

// ---------------------------------------------------------------------------
// Hooki React Query
// ---------------------------------------------------------------------------

const registryInvoicesService = {
  // Pobranie danych prefill z faktury KSeF (funkcja „Księgowanie").
  getKsefAccountingPrefill,

  useGetInvoices: (searchText?: string) =>
    useCustomQuery<IRegistryInvoice[]>(
      [QUERY_KEY, searchText ?? ""],
      () => getInvoicesApi(searchText),
      { retry: false },
    ),

  useGetInvoice: (id: string, enabled = true) =>
    useCustomQuery<IRegistryInvoice>(
      [QUERY_KEY, "byId", id],
      () => getInvoiceApi(id),
      { enabled: enabled && !!id, retry: false },
    ),

  useCreateInvoice: () => {
    const queryClient = useQueryClient();

    return useCustomMutation<IRegistryInvoice, ICreateRegistryInvoicePayload>(
      async (payload) => {
        const { file, ...request } = payload;

        let fileFields: { FileUri: string; KeyId: string } | undefined;
        if (file) {
          // Skan dokumentu rejestrowego — klucz obiektu owinięty kluczem FIRMY
          // (docs/model-kluczy-firmowych.md, I1), nie kluczem aktywnego profilu.
          fileFields = await uploadCompanyEncryptedFile(file);
        }

        return createInvoiceApi({ ...request, ...fileFields });
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries([QUERY_KEY]);
          // Dokument mógł utworzyć rozliczenie zaangażowania — odświeżamy widoki
          // budżetu (jak budgetService.invalidateSettlements).
          queryClient.invalidateQueries(["budget.invoiceSettlements"]);
          queryClient.invalidateQueries(["budget.engagementSettlement"]);
          queryClient.invalidateQueries(["budget.settleableEngagements"]);
          queryClient.invalidateQueries(["budget.allEngagements"]);
          queryClient.invalidateQueries(["budget.expenses"]);
          queryClient.invalidateQueries(["budget.engagementInvoices"]);
          queryClient.invalidateQueries(["budget.invoices"]);
        },
      },
    );
  },
};

export default registryInvoicesService;
